import React, { useState, useEffect } from 'react';
import { 
  Package, 
  X,
  Calendar,
  MapPin,
  CreditCard,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  User,
  Mail,
  Phone,
  ShoppingBag,
  DollarSign,
  FileText,
  Tag,
  Printer
} from 'lucide-react';
import { OrderStatusSelector } from './OrderStatusSelector';
import { OrdersService } from '../../services/orders/orders.service';
import toast from 'react-hot-toast';
import type { PedidoResponse } from '../../services/orders/orders.service';
import { OrderChat } from '../chat/OrderChat';
import { TicketThermal } from '../pos/TicketThermal';
import { printThermalTicket } from '../../utils/print-ticket';

interface OrderDetailsDrawerProps {
  isOpen: boolean;
  pedido: PedidoResponse | null;
  onClose: () => void;
  onStatusChange?: (pedidoId: string, newStatus: string) => void;
  isAdmin?: boolean;
}

const estadosPedido: Record<string, { 
  label: string; 
  color: string; 
  bg: string;
  border: string;
  icono: React.ReactNode;
}> = {
  'PENDING_PAYMENT': { 
    label: 'Pendiente de pago', 
    color: 'text-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icono: <Clock className="w-5 h-5" /> 
  },
  'PAID': { 
    label: 'Pagado', 
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icono: <CreditCard className="w-5 h-5" /> 
  },
  'PROCESSING': { 
    label: 'En preparación', 
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icono: <Package className="w-5 h-5" /> 
  },
  'SHIPPED': { 
    label: 'En camino', 
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    icono: <Truck className="w-5 h-5" /> 
  },
  'DELIVERED': { 
    label: 'Entregado', 
    color: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icono: <CheckCircle className="w-5 h-5" /> 
  },
  'CANCELLED': { 
    label: 'Cancelado', 
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icono: <XCircle className="w-5 h-5" /> 
  },
};

export const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
  isOpen,
  pedido,
  onClose,
  onStatusChange,
  isAdmin = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPedido, setCurrentPedido] = useState<PedidoResponse | null>(null);
  const [mostrarModalAnulacion, setMostrarModalAnulacion] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [loadingAnulacion, setLoadingAnulacion] = useState(false);
  const [mostrarTicketPreview, setMostrarTicketPreview] = useState(false);

  useEffect(() => {
    if (isOpen && pedido) {
      setCurrentPedido(pedido);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, pedido]);

  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentPedido(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatFecha = (fecha?: string) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount == null || isNaN(amount)) return '$0';
    return `$${amount.toLocaleString('es-CO')}`;
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!currentPedido) return;
    await OrdersService.updateStatus(currentPedido.id, newStatus);
    onStatusChange?.(currentPedido.id, newStatus);
  };

  if (!isOpen && !currentPedido) return null;
  if (!currentPedido) return null;

  const estado = estadosPedido[currentPedido.estado];

  return (
    <div className="fixed inset-x-0 bottom-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 flex justify-end overflow-hidden">
        {/* Overlay */}
        <div 
          className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Panel lateral de Factura (a la izquierda del drawer, pegado) */}
        <div 
          className={`h-full flex flex-col bg-white shadow-2xl border-r border-gray-200 transform transition-all duration-300 ease-in-out overflow-hidden ${
            mostrarTicketPreview && currentPedido ? 'w-[420px] translate-x-0 opacity-100' : 'w-0 translate-x-[-20px] opacity-0'
          }`}
        >
          {mostrarTicketPreview && currentPedido && (
            <>
              {/* Header del panel */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h3 className="text-base font-bold text-gray-900">
                  Factura {currentPedido.numeroPedido}
                </h3>
                <button
                  onClick={() => setMostrarTicketPreview(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Contenido del ticket */}
              <div className="flex-1 overflow-y-auto p-5 bg-gray-50 min-w-[420px]">
                <div className="bg-white border rounded-lg p-4 shadow-sm">
                  <TicketThermal
                    id="ticket-thermal-preview"
                    preview
                    numeroPedido={currentPedido.numeroPedido}
                    fecha={new Date(currentPedido.fechas.creadoEn).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
                    vendedor={currentPedido.vendedor?.nombre}
                    cliente={currentPedido.cliente?.nombre}
                    items={currentPedido.items.map(item => ({
                      nombre: item.nombreProducto,
                      presentacion: item.nombrePresentacion,
                      cantidad: item.cantidad,
                      precioUnitario: item.precioUnitario,
                      subtotal: item.subtotal,
                    }))}
                    subtotal={currentPedido.totales.subtotal}
                    iva={0}
                    total={currentPedido.totales.total}
                    metodoPago={currentPedido.metodoPago || (currentPedido.pagos && currentPedido.pagos.length > 0 ? currentPedido.pagos[0].metodo : 'No especificado')}
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0 min-w-[420px]">
                <button
                  onClick={() => setMostrarTicketPreview(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-white text-sm bg-white"
                >
                  Cerrar
                </button>
                <button
                  onClick={() => {
                    const ok = printThermalTicket('ticket-thermal-drawer', currentPedido?.numeroPedido);
                    if (!ok) toast.error('Permita las ventanas emergentes para imprimir');
                  }}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 text-sm flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
              </div>
            </>
          )}
        </div>

        {/* Drawer panel */}
        <section 
          className={`h-full w-[90vw] max-w-[1400px] transform transition-transform duration-300 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div 
            className="h-full flex flex-col shadow-2xl border-l bg-white border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50 border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white">
                  <Package className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Venta {currentPedido.numeroPedido}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Creado el {formatFecha(currentPedido.fechas.creadoEn)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Botón Anular (solo si no está cancelado ni entregado) */}
                {currentPedido.estado !== 'CANCELLED' && currentPedido.estado !== 'DELIVERED' && (
                  <button
                    onClick={() => setMostrarModalAnulacion(true)}
                    className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                  >
                    Anular
                  </button>
                )}
                {/* Selector de estado (solo admin) */}
                {isAdmin && (
                  <div className="w-56">
                    <OrderStatusSelector
                      currentStatus={currentPedido.estado as any}
                      onStatusChange={handleStatusChange}
                      disabled={currentPedido.estado === 'CANCELLED'}
                    />
                  </div>
                )}
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content - Layout de dos columnas */}
            <div className="flex-1 overflow-hidden flex flex-col">
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 flex-1 min-h-0 p-4">
                {/* Columna izquierda - Información del pedido */}
                <div className={`overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent space-y-3 pr-2 ${currentPedido.envio.direccion === 'Venta en local físico' ? 'xl:col-span-2' : ''}`} style={{ maxHeight: 'calc(100vh - 180px)' }}>
                  {/* Estado banner */}
                  <div className={`flex items-center gap-3 p-3 rounded-lg border ${estado?.bg} ${estado?.border}`}>
                    <div className={estado?.color}>
                      {estado?.icono}
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${estado?.color}`}>
                        {estado?.label || currentPedido.estado}
                      </p>
                      {currentPedido.estado === 'CANCELLED' && (
                        <p className="text-xs text-gray-600">
                          Motivo: {currentPedido.motivoCancelacion || 'Sin motivo registrado'}
                        </p>
                      )}
                      {currentPedido.fechas.pagadoEn && (
                        <p className="text-xs text-gray-600">
                          Pagado el {formatFecha(currentPedido.fechas.pagadoEn)}
                        </p>
                      )}
                    </div>
                    <div className={`text-right ${estado?.color}`}>
                      <p className="text-xl font-bold">
                        {formatCurrency(currentPedido.totales.total)}
                      </p>
                    </div>
                  </div>

                  {/* Cliente, Vendedor y Envío */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Cliente */}
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                      <h3 className="font-medium mb-2 flex items-center gap-2 text-sm text-gray-900">
                        <User className="w-4 h-4 text-emerald-500" />
                        Cliente
                      </h3>
                      <div className="space-y-1.5 text-sm text-gray-700">
                        <p className="font-medium">{currentPedido.cliente.nombre}</p>
                        <p className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          {currentPedido.cliente.email}
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          {currentPedido.cliente.telefono}
                        </p>
                        {currentPedido.cliente.tipoDocumento && (
                          <p className="text-xs text-gray-500">
                            <Tag className="w-3 h-3 inline mr-1" />
                            {currentPedido.cliente.tipoDocumento}: {currentPedido.cliente.numeroDocumento}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Vendedor */}
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                      <h3 className="font-medium mb-2 flex items-center gap-2 text-sm text-gray-900">
                        <User className="w-4 h-4 text-blue-500" />
                        Vendedor
                      </h3>
                      <div className="space-y-1.5 text-sm text-gray-700">
                        {currentPedido.vendedor ? (
                          <>
                            <p className="font-medium">{currentPedido.vendedor.nombre}</p>
                            <p className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              {currentPedido.vendedor.email}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">
                            No asignado
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Envío */}
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                      <h3 className="font-medium mb-2 flex items-center gap-2 text-sm text-gray-900">
                        <MapPin className="w-4 h-4 text-emerald-500" />
                        Envío
                      </h3>
                      <div className="space-y-1.5 text-sm text-gray-700">
                        <p>{currentPedido.envio.direccion}</p>
                        <p className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-3.5 h-3.5" />
                          {currentPedido.envio.barrio}, {currentPedido.envio.ciudad}
                        </p>
                        {currentPedido.envio.indicaciones && (
                          <p className="text-xs italic text-gray-500">
                            <FileText className="w-3 h-3 inline mr-1" />
                            {currentPedido.envio.indicaciones}
                          </p>
                        )}
                        {/* Pagos */}
                        {(currentPedido.pagos && currentPedido.pagos.length > 0) ? (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <p className="flex items-center gap-2 text-gray-600 mb-1.5">
                              <CreditCard className="w-4 h-4" />
                              <span className="font-medium">
                                {currentPedido.pagos.length > 1 ? 'Pago mixto' : 'Método de pago'}
                              </span>
                            </p>
                            <div className="space-y-1">
                              {currentPedido.pagos.map((pago, idx) => {
                                // Si viene con la estructura esperada
                                if (pago.metodo && pago.monto != null) {
                                  return (
                                    <div key={idx} className="flex justify-between text-sm">
                                      <span className="text-gray-700">{pago.metodo}</span>
                                      <span className="font-medium text-gray-900">{formatCurrency(pago.monto)}</span>
                                    </div>
                                  );
                                }
                                // Fallback: mostrar las propiedades que tenga el objeto
                                const entries = Object.entries(pago || {});
                                if (entries.length === 0) {
                                  return (
                                    <div key={idx} className="text-sm text-gray-500 italic">
                                      Pago sin información
                                    </div>
                                  );
                                }
                                return (
                                  <div key={idx} className="text-sm space-y-0.5">
                                    {entries.map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-gray-600 capitalize">{key}</span>
                                        <span className="font-medium text-gray-900">
                                          {typeof value === 'number' ? formatCurrency(value) : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : currentPedido.metodoPago && (
                          <p className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-300 text-gray-600">
                            <CreditCard className="w-4 h-4" />
                            Método: <span className="font-medium">{currentPedido.metodoPago}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Productos */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                      <ShoppingBag className="w-4 h-4 text-emerald-500" />
                      Productos ({currentPedido.items.length})
                    </h3>
                    <div className="rounded-xl overflow-hidden border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Producto</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600">Cant.</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Precio</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {currentPedido.items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-4 py-3 text-gray-700">
                                <div>
                                  <p className="font-medium">{item.nombreProducto}</p>
                                  {item.codigoBarras && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      Cód: {item.codigoBarras}
                                    </p>
                                  )}
                                  {item.numeroLote && (
                                    <p className="text-xs text-amber-600 mt-0.5">
                                      Lote: {item.numeroLote}
                                    </p>
                                  )}
                                  {item.proveedor && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      Proveedor: {item.proveedor}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-700">
                                {item.cantidad} <span className="text-xs text-emerald-600 font-medium">({item.nombrePresentacion || 'unidad'})</span>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-700">
                                {formatCurrency(item.precioUnitario)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-gray-900">
                                {formatCurrency(item.subtotal)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totales */}
                  <div className="p-4 rounded-xl border bg-gray-50 border-gray-200">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                      <DollarSign className="w-4 h-4 text-emerald-500" />
                      Resumen
                    </h3>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>{formatCurrency(currentPedido.totales.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Envío</span>
                        <span>
                          {currentPedido.totales.envio === 0 
                            ? <span className="text-green-600 font-medium">Gratis</span> 
                            : formatCurrency(currentPedido.totales.envio)
                          }
                        </span>
                      </div>
                      {currentPedido.totales.descuento > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Descuento</span>
                          <span>-{formatCurrency(currentPedido.totales.descuento)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300 text-gray-900">
                        <span>Total</span>
                        <span>{formatCurrency(currentPedido.totales.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  {(currentPedido.fechas.pagadoEn || currentPedido.fechas.enviadoEn || currentPedido.fechas.entregadoEn) && (
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                        <Calendar className="w-4 h-4 text-emerald-500" />
                        Historial
                      </h3>
                      <div className="p-4 rounded-xl border bg-gray-50 border-gray-200">
                        <div className="space-y-3">
                          {/* Creado */}
                          <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-100">
                            <div className="p-1.5 rounded-md bg-gray-200">
                              <Package className="w-3.5 h-3.5 text-gray-600" />
                            </div>
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-900">
                                Pedido creado
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatFecha(currentPedido.fechas.creadoEn)}
                              </span>
                            </div>
                          </div>

                          {currentPedido.fechas.pagadoEn && (
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                              <div className="p-1.5 rounded-md bg-green-200">
                                <CheckCircle className="w-3.5 h-3.5 text-green-700" />
                              </div>
                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-900">
                                  Pagado
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatFecha(currentPedido.fechas.pagadoEn)}
                                </span>
                              </div>
                            </div>
                          )}

                          {currentPedido.fechas.enviadoEn && (
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-50">
                              <div className="p-1.5 rounded-md bg-blue-200">
                                <Truck className="w-3.5 h-3.5 text-blue-700" />
                              </div>
                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-900">
                                  Enviado
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatFecha(currentPedido.fechas.enviadoEn)}
                                </span>
                              </div>
                            </div>
                          )}

                          {currentPedido.fechas.entregadoEn && (
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-emerald-50">
                              <div className="p-1.5 rounded-md bg-emerald-200">
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-700" />
                              </div>
                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-900">
                                  Entregado
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatFecha(currentPedido.fechas.entregadoEn)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Información del sistema */}
                  <div className="p-4 rounded-xl border bg-gray-100 border-gray-200">
                    <h4 className="text-xs font-medium uppercase tracking-wider mb-2 text-gray-500">
                      Información del Sistema
                    </h4>
                    <div className="space-y-1 text-xs text-gray-600">
                      <p><span className="font-medium">ID:</span> {currentPedido.id}</p>
                      <p><span className="font-medium">Actualizado:</span> {formatFecha(currentPedido.fechas.actualizadoEn)}</p>
                      {currentPedido.wompiTransaccionId && (
                        <p><span className="font-medium">Wompi:</span> {currentPedido.wompiTransaccionId}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna derecha - Chat (solo para pedidos online, no POS) */}
                <div className={`h-full overflow-hidden border-l border-gray-200 ${currentPedido.envio.direccion === 'Venta en local físico' ? 'hidden' : 'block'}`}>
                  {currentPedido.envio.direccion !== 'Venta en local físico' && (
                    <OrderChat
                      pedidoId={currentPedido.id}
                      numeroPedido={currentPedido.numeroPedido}
                      clienteEmail={currentPedido.cliente.email}
                      clienteNombre={currentPedido.cliente.nombre}
                      isAdmin={true}
                      className="h-full"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 border-gray-200">
              <div className="flex gap-3">
                <button 
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-colors bg-gray-200 text-gray-800 hover:bg-gray-300"
                >
                  Cerrar
                </button>
                {!mostrarTicketPreview && (
                  <button 
                    onClick={() => setMostrarTicketPreview(true)}
                    className="flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-colors bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Ver Factura
                  </button>
                )}
              </div>
            </div>
          </div>

        </section>
      </div>

      {/* Ticket térmico oculto para impresión */}
      {currentPedido && (
        <TicketThermal
          id="ticket-thermal-drawer"
          numeroPedido={currentPedido.numeroPedido}
          fecha={new Date(currentPedido.fechas.creadoEn).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
          vendedor={currentPedido.vendedor?.nombre}
          cliente={currentPedido.cliente?.nombre}
          items={currentPedido.items.map(item => ({
            nombre: item.nombreProducto,
            presentacion: item.nombrePresentacion,
            cantidad: item.cantidad,
            precioUnitario: item.precioUnitario,
            subtotal: item.subtotal,
          }))}
          subtotal={currentPedido.totales.subtotal}
          iva={0}
          total={currentPedido.totales.total}
          metodoPago={currentPedido.metodoPago || (currentPedido.pagos && currentPedido.pagos.length > 0 ? currentPedido.pagos[0].metodo : 'No especificado')}
        />
      )}

      {/* Modal de Anulación */}
      {mostrarModalAnulacion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-6 bg-white">
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              Anular Pedido
            </h3>
            <p className="text-sm mb-4 text-gray-600">
              Pedido: <span className="font-mono font-bold">{currentPedido?.numeroPedido}</span>
            </p>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Motivo de la anulación
            </label>
            <textarea
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              placeholder="Ej: Cliente se equivocó de producto"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white border-gray-300 text-gray-900"
              rows={3}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setMostrarModalAnulacion(false);
                  setMotivoAnulacion('');
                }}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 text-sm"
              >
                Cancelar
              </button>
              <button
                disabled={!motivoAnulacion.trim() || loadingAnulacion}
                onClick={async () => {
                  if (!currentPedido || !motivoAnulacion.trim()) return;
                  setLoadingAnulacion(true);
                  try {
                    await OrdersService.updateStatus(currentPedido.id, 'CANCELLED', motivoAnulacion.trim());
                    setCurrentPedido({ ...currentPedido, estado: 'CANCELLED', motivoCancelacion: motivoAnulacion.trim() });
                    onStatusChange?.(currentPedido.id, 'CANCELLED');
                    setMostrarModalAnulacion(false);
                    setMotivoAnulacion('');
                  } catch (err: any) {
                    toast.error(err.message || 'Error al anular el pedido');
                  } finally {
                    setLoadingAnulacion(false);
                  }
                }}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {loadingAnulacion ? 'Anulando...' : 'Confirmar Anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsDrawer;
